import type { DiscountDTO, MinorUnit, Option, StorefrontOrderLine, SveaOrderRow } from 'ecosystem'
import {
  type BuyerVatRates,
  createMinorUnit,
  getTax,
  getVatRateByCountryCode,
  shippingForBuyer,
  storeGrossRebaser,
  sumArrayOfNumbers
} from 'shared-utils'
import { orderLineValueCalculator } from '../../payment'

/**
 * Svea order item name has a limit of 40 symbols
 */
const createOrderItemName = (name: string): SveaOrderRow['Name'] => {
  return name.substring(0, 40)
}

/**
 * Svea order item unit has a limit of 4 symbols
 */
const createOrderItemUnit = (name: string): SveaOrderRow['Unit'] => {
  return name.substring(0, 4)
}

const mapOrderLinesToSveaOrderItems = (
  items: StorefrontOrderLine[],
  cartDiscountCode: DiscountDTO | null
): SveaOrderRow[] => {
  return items.map((item) => {
    const { product, quantity, vatRate } = item
    const taxRate = createMinorUnit(vatRate)
    const { unitPrice, totalDiscountAmount } = orderLineValueCalculator(item, cartDiscountCode)
    return {
      ArticleNumber: product.articleNumber,
      Name: createOrderItemName(product.name),
      Quantity: createMinorUnit(quantity),
      Unit: createOrderItemUnit('pcs'),
      UnitPrice: unitPrice,
      VatPercent: taxRate,
      DiscountAmount: totalDiscountAmount,
      RowType: 'Row'
    } satisfies SveaOrderRow
  })
}

const servicesOrderLineFactory = (
  items: StorefrontOrderLine[],
  taxRate: MinorUnit,
  rebaseServicePrice: (grossMajor: number) => number
): SveaOrderRow[] => {
  return items
    .map((item) =>
      item.services.map((service) => {
        const quantity = service.quantity
        let unitPrice = createMinorUnit(rebaseServicePrice(service.service.price))
        const discountFactor = createMinorUnit(rebaseServicePrice(service.service.discountPrice))
        let totalDiscountAmount = 0
        let totalAmount = 0

        /*
         Basic formula for calculating price of service:
         service.price + service.discountPrice * (quantity - 1)
         */
        if (quantity > 0) {
          totalAmount = unitPrice + (quantity - 1) * discountFactor

          if (!unitPrice && discountFactor) {
            unitPrice = discountFactor
          }

          totalDiscountAmount = unitPrice * quantity - totalAmount
        }

        return {
          ArticleNumber: service.service.id,
          Name: createOrderItemName(service.service.name),
          Quantity: createMinorUnit(quantity),
          Unit: createOrderItemUnit('service'),
          UnitPrice: unitPrice,
          VatPercent: taxRate,
          DiscountAmount: totalDiscountAmount,
          RowType: 'Row'
        } satisfies SveaOrderRow
      })
    )
    .flat()
}

export const sveaOrderLineFactory = (
  items: StorefrontOrderLine[],
  cartDiscountCode: DiscountDTO | null,
  countryCode?: string,
  buyerVat?: Option<BuyerVatRates>
) => {
  // Product rows carry each line's own `vatRate`, already resolved for the
  // buyer upstream. Service rows have no rate of their own, so they take the
  // store's rate — resolved here for the same buyer so the two agree.
  const domesticRate = getVatRateByCountryCode(countryCode)
  const taxRate = createMinorUnit(buyerVat?.resolveRate(domesticRate) ?? domesticRate)
  const productOrderLines = mapOrderLinesToSveaOrderItems(items, cartDiscountCode)
  const serviceOrderLines = servicesOrderLineFactory(
    items,
    taxRate,
    storeGrossRebaser(domesticRate, buyerVat)
  )
  const orderLines = [...productOrderLines, ...serviceOrderLines]
  const orderAmount = sumArrayOfNumbers(
    orderLines.map((item) => item.UnitPrice * (item.Quantity / 100) - (item.DiscountAmount ?? 0))
  )
  // The same basket net of VAT, in the units `orderAmount` uses. Feeds
  // `netOrderAmount` on the shipping factories so a delivery rate configured
  // ex-VAT is evaluated against the figure it was written on. Derived from the
  // rows rather than from the basket, so the discount code and this provider's
  // unit convention are already applied.
  const netOrderAmount = sumArrayOfNumbers(
    orderLines.map((item) => {
      const gross = item.UnitPrice * (item.Quantity / 100) - (item.DiscountAmount ?? 0)
      return gross - getTax(gross, item.VatPercent ?? 0, true)
    })
  )
  return {
    orderLines,
    productOrderLines,
    orderAmount,
    netOrderAmount,
    taxRate,
    /** Move a carrier's VAT-inclusive quote onto this buyer's rate. Unit-agnostic
     *  (a pure ratio), so minor units pass through unchanged. */
    rebaseShipping: (grossQuote: number) =>
      shippingForBuyer(grossQuote, domesticRate, buyerVat).cost
  }
}
